<template>
  <div class="silo-result-table-graph-root">
    <div
      v-if="!siloMapHowToDismissed"
      class="silo-map-how-to"
      role="status"
    >
      <button
        type="button"
        class="silo-map-how-to__close"
        aria-label="Dismiss silo map help"
        @click="dismissSiloMapHowTo"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
      <div class="silo-map-how-to__header">
        <div class="silo-map-how-to__icon-wrap" aria-hidden="true">
          <i class="material-icons silo-map-how-to__share-icon">share</i>
        </div>
        <h3 class="silo-map-how-to__title">How to use the silo map</h3>
      </div>
      <p class="silo-map-how-to__body">
        This view shows how Top-Level and supporting pages should be internally linked to maximize the performance of your topic cluster. The arrows represent the internal links between pages. Once pages are published on your site use this visual to map the internal links between them.
      </p>
      <p class="silo-map-how-to__tip">
        ! We recommend in text paragraph links using partial match anchor text.
      </p>
    </div>
    <div class="wrapper">
    <div class="graph-margin-auto">
      <div id="graph-main" class="graph-main" ref="graph-main">
        <div class="side-column left-column" ref="left-column">
          <template v-for="keyword, idx in graphData.left">
            <div class="keyword-item supporting" :ref="'left' + idx" :key="idx + 'keyword'">
              <div class="keyword-item-tag">S</div>
              <div class="keyword">{{ keyword }}</div>
              <div class="keyword-type">supporting keyword</div>
            </div>
            <img
              v-if="idx < graphData.left.length - 1"
              :key="idx + 'double-arrows'" width="20" height="26" src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTMiIHZpZXdCb3g9IjAgMCAxMCAxMyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTguMDA1ODkgMTIuNTE5MUw5LjY0MTM3IDkuNjg2MzZMNi4zNzA0MSA5LjY4NjM2TDguMDA1ODkgMTIuNTE5MVpNNy43MjI2MiAwLjE1NjM3Mkw3LjcyMjYyIDkuOTY5NjNMOC4yODkxNiA5Ljk2OTY0TDguMjg5MTYgMC4xNTYzNzJMNy43MjI2MiAwLjE1NjM3MloiIGZpbGw9IiNCMkIyQjIiLz4KPHBhdGggZD0iTTIuMzk0MTIgMC4xNTYzNzFMMC43NTg2NDUgMi45ODkxTDQuMDI5NiAyLjk4OTFMMi4zOTQxMiAwLjE1NjM3MVpNMi42NzczOSAxMi41MTkxTDIuNjc3MzkgMi43MDU4M0wyLjExMDg1IDIuNzA1ODNMMi4xMTA4NSAxMi41MTkxTDIuNjc3MzkgMTIuNTE5MVoiIGZpbGw9IiNCMkIyQjIiLz4KPC9zdmc+Cg=="
            />
          </template>
        </div>

        <svg class="left-arrows" width="100" :height="arrowsSVGHeight">
          <template>
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="8.7" refY="3" orient="auto" markerUnits="strokeWidth" fill="#B2B2B2">
                <path d="M0,0 L0,6 L9,3 z" />
              </marker>
              <marker id="arrow-start" markerWidth="10" markerHeight="10" refX="0.7" refY="3" orient="auto" markerUnits="strokeWidth" fill="#B2B2B2">
                <path d="M 9 0 L 9 6 L 0 3 z" />
              </marker>
            </defs>
            <template
              v-for="keyword, idx in graphData.left"
            >
              <line
                :key="idx"
                v-bind="{
                  x1: 0 + 5,
                  y1: arrowY1Map['left' + idx],
                  x2: 100 - 5,
                  y2: (
                    (arrowsSVGHeight / 2)       // height center
                    + ((idx - (graphData.left.length / 2)) * 4)   // + some 4px vertical spacing for arrow heads depending on idx
                  )
                }"
                stroke="#B2B2B2"
                marker-end="url(#arrow)"
              />
              <line
                v-if="idx === 0"
                :key="idx + 'extra-arrow'"
                v-bind="{
                  x1: 0 + 5,
                  y1: arrowY1Map['left' + idx] - 6,
                  x2: 100 - 5,
                  y2: (
                    (arrowsSVGHeight / 2)       // height center
                    + ((idx - (graphData.left.length / 2)) * 4)   // + some 4px vertical spacing for arrow heads depending on idx
                  ) - 6
                }"
                stroke="#B2B2B2"
                marker-start="url(#arrow-start)"
              />
            </template>
          </template>
        </svg>

        <div class="center-column" ref="center-column">
          <div v-if="graphData.center" class="keyword-item top-level" ref="center-item">
            <div class="keyword-item-tag">TL</div>
            <div class="keyword">{{ graphData.center }}</div>
            <div class="keyword-type">top-level keyword</div>
          </div>
          <div v-else class="keyword-item top-level" ref="center-item">
            <div class="keyword-item-tag">T</div>
            <div class="keyword">Top-level keyword</div>
            <div class="keyword-type"></div>
          </div>
        </div>

        <svg class="right-arrows" width="100" :height="arrowsSVGHeight">
          <template>
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="8.7" refY="3" orient="auto" markerUnits="strokeWidth" fill="#B2B2B2">
                <path d="M0,0 L0,6 L9,3 z" />
              </marker>
            </defs>
            <line
              v-for="keyword, idx in graphData.right"
              :key="idx"
              v-bind="{
                x1: 100 - 5,
                y1: arrowY1Map['right' + idx],
                x2: 0 + 5,
                y2: (
                  (arrowsSVGHeight / 2)       // height center
                  + ((idx - (graphData.right.length / 2)) * 4)   // + some 4px vertical spacing for arrow heads depending on idx
                )
              }"
              stroke="#B2B2B2"
              marker-end="url(#arrow)"
            />
          </template>
        </svg>

        <div class="side-column right-column" ref="right-column">
          <template v-for="keyword, idx in graphData.right">
            <div class="keyword-item supporting" :ref="'right' + idx" :key="idx + 'keyword'">
              <div class="keyword-item-tag">S</div>
              <div class="keyword">{{ keyword }}</div>
              <div class="keyword-type">supporting keyword</div>
            </div>
            <img
              v-if="idx < graphData.right.length - 1"
              :key="idx + 'double-arrows'" width="20" height="26" src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTMiIHZpZXdCb3g9IjAgMCAxMCAxMyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTguMDA1ODkgMTIuNTE5MUw5LjY0MTM3IDkuNjg2MzZMNi4zNzA0MSA5LjY4NjM2TDguMDA1ODkgMTIuNTE5MVpNNy43MjI2MiAwLjE1NjM3Mkw3LjcyMjYyIDkuOTY5NjNMOC4yODkxNiA5Ljk2OTY0TDguMjg5MTYgMC4xNTYzNzJMNy43MjI2MiAwLjE1NjM3MloiIGZpbGw9IiNCMkIyQjIiLz4KPHBhdGggZD0iTTIuMzk0MTIgMC4xNTYzNzFMMC43NTg2NDUgMi45ODkxTDQuMDI5NiAyLjk4OTFMMi4zOTQxMiAwLjE1NjM3MVpNMi42NzczOSAxMi41MTkxTDIuNjc3MzkgMi43MDU4M0wyLjExMDg1IDIuNzA1ODNMMi4xMTA4NSAxMi41MTkxTDIuNjc3MzkgMTIuNTE5MVoiIGZpbGw9IiNCMkIyQjIiLz4KPC9zdmc+Cg=="
            />
          </template>
        </div>

        <div v-if="graphData?.right?.length" class="bottom-arrows">
          <svg :width="bottomArrowsSVGWidth" :height="20">
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="8.7" refY="3" orient="auto" markerUnits="strokeWidth" fill="#B2B2B2">
                <path d="M0,0 L0,6 L9,3 z" />
              </marker>
            </defs>
            <line
              v-bind="{
                x1: 5,
                y1: 7,
                x2: bottomArrowsSVGWidth - 5,
                y2: 7
              }"
              stroke="#B2B2B2"
              stroke-width="1"
              marker-end="url(#arrow)"
            />
            <line
              v-bind="{
                x1: bottomArrowsSVGWidth - 5,
                y1: 13,
                x2: 5,
                y2: 13
              }"
              stroke="#B2B2B2"
              stroke-width="1"
              marker-end="url(#arrow)"
            />
          </svg>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>

const SILO_MAP_HOW_TO_STORAGE_KEY = 'pop_siloMapHowToDismissed';

export default {
  props: {
    graphData: {
      type: Object,
    }
  },
  methods: {
    calculateLayout() {
      if (this.$refs['graph-main'] && this.$refs['center-column']) {
        this.arrowsSVGHeight = this.$refs['graph-main'].scrollHeight
        this.bottomArrowsSVGWidth = this.$refs['center-column'].scrollWidth + 200;
        ['left', 'right'].forEach(side => {
          this.graphData[side].forEach((keyword, idx) => {
            const element = this.$refs[side + idx][0]
            const y1 = element.offsetTop + (element.scrollHeight / 2)
  
            const key = side + idx
  
            this.arrowY1Map = {
              ...this.arrowY1Map,
              [key]: y1
            }
          })
        })
      }
    },
    syncSiloMapHowToFromStorage() {
      try {
        if (typeof localStorage === 'undefined') {
          this.siloMapHowToDismissed = false;
          return;
        }
        this.siloMapHowToDismissed = localStorage.getItem(SILO_MAP_HOW_TO_STORAGE_KEY) === '1';
      } catch (e) {
        this.siloMapHowToDismissed = false;
      }
    },
    dismissSiloMapHowTo() {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(SILO_MAP_HOW_TO_STORAGE_KEY, '1');
        }
      } catch (e) {
        /* ignore quota / private mode */
      }
      this.siloMapHowToDismissed = true;
    },
  },
  data() {
    return {
      arrowsSVGHeight: 100,
      bottomArrowsSVGWidth: 300,
      arrowY1Map: {},
      siloMapHowToDismissed: false,
    }
  },
  created() {
    this.syncSiloMapHowToFromStorage();
  },
  mounted () {
    // wait for $refs to be populated
    this.$nextTick()
    .then(() => {
      this.calculateLayout()
    })
  }
}
</script>

<style lang="scss">
.silo-result-table-graph-root {
  display: flex;
  flex-direction: column;
}

.silo-map-how-to {
  position: relative;
  box-sizing: border-box;
  max-width: 1080px;
  width: 100%;
  padding: 16px 44px 16px 16px;
  border-radius: 12px;
  background: #f7f5fb;
}

.silo-map-how-to__close {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #014751;
  cursor: pointer;
}

.silo-map-how-to__close:hover {
  background: rgba(1, 71, 81, 0.08);
}

.silo-map-how-to__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding-right: 8px;
}

.silo-map-how-to__icon-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #ffeddd;
}

.silo-map-how-to__share-icon {
  font-size: 22px;
  color: #ef6111;
  line-height: 1;
}

.silo-map-how-to__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #014751;
  line-height: 1.3;
}

.silo-map-how-to__body {
  margin: 0 0 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #1b4650;
}

.silo-map-how-to__tip {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  font-weight: 500;
  color: #b86800;
}

.wrapper {
  background: #fff;
}

.graph-margin-auto {
  margin: 0 auto;
  overflow-x: auto;
  width: 1080px;
  padding: 40px;
}

.graph-main {
  width: 1000px;

  background: #fff;

  display: flex;
  flex-direction: row;
  align-items: stretch;

  position: relative;
  
  .side-column, .center-column {
    flex: 1;
  }

  .side-column {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    justify-content: flex-end;
    min-height: 160px;
  }

  .center-column {
    align-self: center;
  }

  .bottom-arrows {
    position: absolute;
    bottom: 10px;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .keyword-item {

    width: 100%;
    
    border-radius: 10px;
    padding: 15px 40px 15px 15px;
    box-sizing: border-box;
    // box-shadow: 0px 2px 7px 0px rgba(0, 0, 0, 0.1);

    position: relative;

    .keyword-item-tag {
      position: absolute;
      top: 13px;
      right: 10px;
      border-radius: 3px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 20px;
    }

    .keyword {
      font-weight: 700;
      font-size: 0.9rem;
      margin-bottom: 5px;
    }

    .keyword-type {
      font-size: 0.8rem;
    }

    &.supporting {
      background-color: transparent;
      border: 1px solid #FF4040;

      .keyword-item-tag {
        color: #FF4040;
        background: #FF40401A;
      }

      .keyword {
        color: #014751;
      }

      .keyword-type {
        color: #014751;
      }
    }

    &.top-level {
      background-color: #11A33D1A;
      border: 1px solid #11A33D;

      .keyword-item-tag {
        color: #11A33D;
        background: #11A33D33;
      }

      .keyword {
        color: #014751;
      }

      .keyword-type {
        color: #014751;
      }
    }
  }
}


</style>
